import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import confetti from 'canvas-confetti';

const { FiCheckCircle, FiArrowRight, FiStar, FiRocket } = FiIcons;

const Welcome = () => {
  const { lang } = useLanguage();
  const t = translations[lang].welcome;
  const p = translations[lang].paths;

  useEffect(() => {
    // Check if this is the first visit to the welcome page
    const hasSeenConfetti = localStorage.getItem('has_seen_welcome_confetti');
    
    if (!hasSeenConfetti) {
      // Fire a nice confetti shower
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      // Mark as seen
      localStorage.setItem('has_seen_welcome_confetti', 'true');
    }
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full bg-white rounded-3xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-center text-white">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.2 }}
            className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6"
          >
            <SafeIcon icon={FiRocket} className="text-4xl" />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t.title}</h1>
          <p className="text-blue-100 text-lg">{t.subtitle}</p>
        </div>

        <div className="p-8 md:p-12">
          <div className="space-y-6 mb-10">
            {t.benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + (index * 0.1) }}
                className="flex items-start space-x-4"
              >
                <div className="mt-1 bg-green-100 p-1 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 text-lg" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-2xl p-6 mb-10 flex items-center justify-between border border-blue-100">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-600 p-3 rounded-xl shadow-lg shadow-blue-200">
                <SafeIcon icon={FiStar} className="text-white text-xl" />
              </div>
              <div>
                <p className="text-blue-900 font-bold">{t.premiumStatus.title}</p>
                <p className="text-blue-700 text-sm">{t.premiumStatus.description}</p>
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                Active
              </span>
            </div>
          </div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link 
              to={p.steps}
              className="group w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-2xl font-bold text-lg flex items-center justify-center space-x-3 shadow-lg shadow-blue-200 hover:shadow-xl transition-all"
            >
              <span>{t.cta}</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          
          <p className="text-center text-gray-500 text-sm mt-6">
            {t.footer}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Welcome;